// import dagre from "dagre";
//
// // -------------------------------------------------------------------
// // Dimensions — tune these to match your MemberNode CSS
// // -------------------------------------------------------------------
// const NODE_WIDTH = 280;   // Approximate width of a single member pill
// const NODE_HEIGHT = 85;   // Matches min-h-[10vh] on MemberNode
// const COUPLE_WIDTH = 620; // Two pills + label + gap between them
// const COUPLE_HEIGHT = 85;
//
// // -------------------------------------------------------------------
// // Relationship classification (matches relation.jsx)
// // -------------------------------------------------------------------
// const SPOUSE_RELATIONS = new Set(["husband-wife", "wife-husband"]);
//
// // Parent-type: source is the PARENT
// const PARENT_TO_CHILD = new Set([
// 	"father", "mother", "father-in-law", "mother-in-law",
// ]);
//
// // Child-type: source is the CHILD (we reverse for Dagre)
// const CHILD_TO_PARENT = new Set([
// 	"son", "daughter", "son-in-law", "daughter-in-law",
// 	"adopted-son", "adopted-daughter",
// ]);
//
// // Horizontal: siblings, in-law siblings — same generation, not hierarchy
// const HORIZONTAL_RELATIONS = new Set([
// 	"siblings", "brother", "sister",
// 	"brother-in-law", "sister-in-law",
// ]);
//
// /**
//  * Computes a hierarchical family tree layout using Dagre.
//  *
//  * Strategy:
//  * 1. Scan all edges to identify spouse pairs.
//  * 2. Merge each spouse pair into a single "couple" compound node for Dagre.
//  * 3. Build parent→child edges using the couple node as the parent.
//  * 4. Run Dagre to get generational (Y) and sibling (X) positions.
//  * 5. Post-process: split each couple node back into two side-by-side members.
//  * 6. Center children under their parent couple.
//  *
//  * @param {Array} nodes  - React Flow nodes from DB
//  * @param {Array} edges  - React Flow edges from DB
//  * @param {Object} opts  - Optional layout config overrides
//  * @returns {{ nodes: Array, edges: Array }}
//  */
// export const computeDagreLayout = (nodes, edges, opts = {}) => {
// 	const {
// 		direction = "TB",
// 		nodeSpacing = 80,       // Gap between sibling nodes
// 		rankSpacing = 180,      // Gap between generations
// 		coupleGap = 60,         // Gap between spouses when split
// 	} = opts;
//
// 	if (!nodes.length) return {nodes, edges};
//
// 	// Build a quick lookup: nodeId → node
// 	const nodeMap = {};
// 	nodes.forEach((n) => {
// 		const id = n.id || n._id;
// 		nodeMap[id] = n;
// 	});
//
// 	// ---------------------------------------------------------------
// 	// STEP 1: Find all spouse pairs
// 	// ---------------------------------------------------------------
// 	const spousePairs = [];         // Array of [idA, idB]
// 	const nodeToCouple = {};        // nodeId → coupleId
// 	const processedSpouseEdges = new Set();
//
// 	edges.forEach((edge) => {
// 		const rel = edge.data?.relationshipType;
// 		if (!SPOUSE_RELATIONS.has(rel)) return;
//
// 		const pairKey = [edge.source, edge.target].sort().join("::");
// 		if (processedSpouseEdges.has(pairKey)) return;
// 		processedSpouseEdges.add(pairKey);
//
// 		const coupleId = `couple::${edge.source}::${edge.target}`;
// 		spousePairs.push({
// 			id: coupleId,
// 			memberA: edge.source,
// 			memberB: edge.target,
// 		});
// 		nodeToCouple[edge.source] = coupleId;
// 		nodeToCouple[edge.target] = coupleId;
// 	});
//
// 	// ---------------------------------------------------------------
// 	// STEP 2: Build Dagre graph with compound couple nodes
// 	// ---------------------------------------------------------------
// 	const g = new dagre.graphlib.Graph();
// 	g.setDefaultEdgeLabel(() => ({}));
// 	g.setGraph({
// 		rankdir: direction,
// 		nodesep: nodeSpacing,
// 		ranksep: rankSpacing,
// 		edgesep: 50,
// 		marginx: 60,
// 		marginy: 60,
// 	});
//
// 	// Track which individual nodes are part of a couple
// 	const inCouple = new Set();
// 	spousePairs.forEach((couple) => {
// 		inCouple.add(couple.memberA);
// 		inCouple.add(couple.memberB);
// 		// Add the couple as a single wide node
// 		g.setNode(couple.id, {width: COUPLE_WIDTH, height: COUPLE_HEIGHT});
// 	});
//
// 	// Add all singleton nodes (not part of any couple)
// 	nodes.forEach((node) => {
// 		const id = node.id || node._id;
// 		if (!inCouple.has(id)) {
// 			g.setNode(id, {width: NODE_WIDTH, height: NODE_HEIGHT});
// 		}
// 	});
//
// 	// ---------------------------------------------------------------
// 	// STEP 3: Add hierarchical edges (parent→child only)
// 	//         Map individual parent IDs to their couple node if applicable
// 	// ---------------------------------------------------------------
// 	const resolveNode = (id) => nodeToCouple[id] || id;
// 	const addedEdges = new Set();
//
// 	edges.forEach((edge) => {
// 		const rel = edge.data?.relationshipType;
// 		if (!rel) return;
//
// 		// Skip spouse and horizontal relations — not hierarchy
// 		if (SPOUSE_RELATIONS.has(rel) || HORIZONTAL_RELATIONS.has(rel)) return;
//
// 		let parentId, childId;
//
// 		if (PARENT_TO_CHILD.has(rel)) {
// 			parentId = edge.source;
// 			childId = edge.target;
// 		} else if (CHILD_TO_PARENT.has(rel)) {
// 			parentId = edge.target;
// 			childId = edge.source;
// 		} else {
// 			return; // Unknown relationship type
// 		}
//
// 		// Resolve to couple node if parent is in a couple
// 		const resolvedParent = resolveNode(parentId);
// 		const resolvedChild = resolveNode(childId);
//
// 		// Don't create self-edges (child married to parent's spouse's family)
// 		if (resolvedParent === resolvedChild) return;
//
// 		const edgeKey = `${resolvedParent}→${resolvedChild}`;
// 		if (addedEdges.has(edgeKey)) return;
// 		addedEdges.add(edgeKey);
//
// 		// Only add if both nodes exist in the graph
// 		if (g.hasNode(resolvedParent) && g.hasNode(resolvedChild)) {
// 			g.setEdge(resolvedParent, resolvedChild, {weight: 2});
// 		}
// 	});
//
// 	// ---------------------------------------------------------------
// 	// STEP 4: Run Dagre layout
// 	// ---------------------------------------------------------------
// 	dagre.layout(g);
//
// 	// ---------------------------------------------------------------
// 	// STEP 5: Extract positions & split couples back into individuals
// 	// ---------------------------------------------------------------
// 	const posMap = {};
//
// 	// Handle singleton nodes (not in any couple)
// 	nodes.forEach((node) => {
// 		const id = node.id || node._id;
// 		if (inCouple.has(id)) return;
//
// 		const dagreNode = g.node(id);
// 		if (dagreNode) {
// 			posMap[id] = {
// 				x: dagreNode.x - NODE_WIDTH / 2,
// 				y: dagreNode.y - NODE_HEIGHT / 2,
// 			};
// 		} else {
// 			posMap[id] = node.position || {x: 0, y: 0};
// 		}
// 	});
//
// 	// Handle couple nodes — split into two members side by side
// 	spousePairs.forEach((couple) => {
// 		const dagreNode = g.node(couple.id);
// 		if (!dagreNode) {
// 			// Fallback
// 			posMap[couple.memberA] = nodeMap[couple.memberA]?.position || {x: 0, y: 0};
// 			posMap[couple.memberB] = nodeMap[couple.memberB]?.position || {x: 0, y: 0};
// 			return;
// 		}
//
// 		const centerX = dagreNode.x;
// 		const centerY = dagreNode.y - NODE_HEIGHT / 2;
//
// 		// Place member A on the left, member B on the right
// 		posMap[couple.memberA] = {
// 			x: centerX - NODE_WIDTH - coupleGap / 2,
// 			y: centerY,
// 		};
// 		posMap[couple.memberB] = {
// 			x: centerX + coupleGap / 2,
// 			y: centerY,
// 		};
// 	});
//
// 	// ---------------------------------------------------------------
// 	// STEP 6: Center children under their parent couple/node
// 	//         (Fine-tune — Dagre already does rough centering,
// 	//          but this corrects drift from compound node splitting)
// 	// ---------------------------------------------------------------
// 	// Build parent→children mapping using resolved (couple) IDs
// 	const parentToChildren = {};
//
// 	edges.forEach((edge) => {
// 		const rel = edge.data?.relationshipType;
// 		if (!rel) return;
// 		if (SPOUSE_RELATIONS.has(rel) || HORIZONTAL_RELATIONS.has(rel)) return;
//
// 		let parentId, childId;
// 		if (PARENT_TO_CHILD.has(rel)) {
// 			parentId = edge.source;
// 			childId = edge.target;
// 		} else if (CHILD_TO_PARENT.has(rel)) {
// 			parentId = edge.target;
// 			childId = edge.source;
// 		} else {
// 			return;
// 		}
//
// 		const resolvedParent = resolveNode(parentId);
// 		// Child should be resolved too — if child is in a couple,
// 		// we want to center the couple, not the individual
// 		const resolvedChild = resolveNode(childId);
//
// 		if (resolvedParent === resolvedChild) return;
//
// 		if (!parentToChildren[resolvedParent]) {
// 			parentToChildren[resolvedParent] = new Set();
// 		}
// 		parentToChildren[resolvedParent].add(resolvedChild);
// 	});
//
// 	// For each parent, compute parent center X and shift children to be centered
// 	Object.entries(parentToChildren).forEach(([parentKey, childSet]) => {
// 		const children = [...childSet];
// 		if (children.length === 0) return;
//
// 		// Get parent center X
// 		let parentCenterX;
// 		if (parentKey.startsWith("couple::")) {
// 			// Find the couple
// 			const couple = spousePairs.find((c) => c.id === parentKey);
// 			if (couple && posMap[couple.memberA] && posMap[couple.memberB]) {
// 				parentCenterX =
// 					(posMap[couple.memberA].x + posMap[couple.memberB].x + NODE_WIDTH) / 2;
// 			}
// 		} else {
// 			if (posMap[parentKey]) {
// 				parentCenterX = posMap[parentKey].x + NODE_WIDTH / 2;
// 			}
// 		}
//
// 		if (parentCenterX === undefined) return;
//
// 		// Get all individual member IDs that are children
// 		// (expand couple children back to their two members)
// 		const childMemberIds = [];
// 		children.forEach((childKey) => {
// 			if (childKey.startsWith("couple::")) {
// 				const couple = spousePairs.find((c) => c.id === childKey);
// 				if (couple) {
// 					childMemberIds.push({
// 						type: "couple",
// 						memberA: couple.memberA,
// 						memberB: couple.memberB,
// 					});
// 				}
// 			} else {
// 				childMemberIds.push({type: "single", id: childKey});
// 			}
// 		});
//
// 		// Calculate the total width of all children as laid out
// 		// and the current center
// 		let minX = Infinity, maxX = -Infinity;
// 		childMemberIds.forEach((child) => {
// 			if (child.type === "couple") {
// 				const posA = posMap[child.memberA];
// 				const posB = posMap[child.memberB];
// 				if (posA) minX = Math.min(minX, posA.x);
// 				if (posB) maxX = Math.max(maxX, posB.x + NODE_WIDTH);
// 				if (posA) maxX = Math.max(maxX, posA.x + NODE_WIDTH);
// 				if (posB) minX = Math.min(minX, posB.x);
// 			} else {
// 				const pos = posMap[child.id];
// 				if (pos) {
// 					minX = Math.min(minX, pos.x);
// 					maxX = Math.max(maxX, pos.x + NODE_WIDTH);
// 				}
// 			}
// 		});
//
// 		if (minX === Infinity) return;
//
// 		const childrenCenterX = (minX + maxX) / 2;
// 		const shiftX = parentCenterX - childrenCenterX;
//
// 		// Apply the shift to all child members
// 		childMemberIds.forEach((child) => {
// 			if (child.type === "couple") {
// 				if (posMap[child.memberA]) posMap[child.memberA].x += shiftX;
// 				if (posMap[child.memberB]) posMap[child.memberB].x += shiftX;
// 			} else {
// 				if (posMap[child.id]) posMap[child.id].x += shiftX;
// 			}
// 		});
// 	});
//
// 	// ---------------------------------------------------------------
// 	// STEP 7: Apply positions to nodes
// 	// ---------------------------------------------------------------
// 	const layoutedNodes = nodes.map((node) => {
// 		const id = node.id || node._id;
// 		return {
// 			...node,
// 			position: posMap[id] || node.position || {x: 0, y: 0},
// 		};
// 	});
//
// 	return {nodes: layoutedNodes, edges};
// };

// import dagre from "dagre";
//
// // -------------------------------------------------------------------
// // Default dimensions — used as fallback when actual sizes unavailable
// // -------------------------------------------------------------------
// const DEFAULT_NODE_WIDTH = 280;
// const DEFAULT_NODE_HEIGHT = 85;
// const COUPLE_LABEL_WIDTH = 120; // Space for "Husband - Wife" label between spouses
//
// // -------------------------------------------------------------------
// // Relationship classification (matches relation.jsx)
// // -------------------------------------------------------------------
// const SPOUSE_RELATIONS = new Set(["husband-wife", "wife-husband"]);
//
// const PARENT_TO_CHILD = new Set([
// 	"father", "mother", "father-in-law", "mother-in-law",
// ]);
//
// const CHILD_TO_PARENT = new Set([
// 	"son", "daughter", "son-in-law", "daughter-in-law",
// 	"adopted-son", "adopted-daughter",
// ]);
//
// const HORIZONTAL_RELATIONS = new Set([
// 	"siblings", "brother", "sister",
// 	"brother-in-law", "sister-in-law",
// ]);
//
// // -------------------------------------------------------------------
// // Helpers
// // -------------------------------------------------------------------
//
// /**
//  * Estimate node width from data. If the React Flow instance is available
//  * and the node has been rendered, use the actual measured width.
//  * Otherwise, estimate from name length.
//  */
// const estimateNodeWidth = (node, rfInstance) => {
// 	const id = node.id || node._id;
//
// 	// Try to get actual rendered width from React Flow
// 	if (rfInstance) {
// 		const rfNode = rfInstance.getNode(id);
// 		if (rfNode?.width) return rfNode.width;
// 	}
//
// 	// Estimate from name length (each char ~9px, min 220, max 340)
// 	const firstName = node.data?.firstName || "";
// 	const middleName = node.data?.middleName || "";
// 	const fullName = `${firstName} ${middleName}`;
// 	const estimated = Math.max(220, Math.min(340, fullName.length * 9 + 120));
// 	return estimated;
// };
//
// const estimateNodeHeight = (node, rfInstance) => {
// 	const id = node.id || node._id;
// 	if (rfInstance) {
// 		const rfNode = rfInstance.getNode(id);
// 		if (rfNode?.height) return rfNode.height;
// 	}
// 	return DEFAULT_NODE_HEIGHT;
// };
//
// /**
//  * Determine consistent husband/wife ordering from the spouse edge.
//  * "husband-wife" means source=husband, target=wife.
//  * Returns { husbandId, wifeId }
//  */
// const getSpouseOrder = (edges, idA, idB) => {
// 	for (const edge of edges) {
// 		const rel = edge.data?.relationshipType;
// 		if (rel === "husband-wife") {
// 			if (edge.source === idA && edge.target === idB) {
// 				return {husbandId: idA, wifeId: idB};
// 			}
// 			if (edge.source === idB && edge.target === idA) {
// 				return {husbandId: idB, wifeId: idA};
// 			}
// 		}
// 	}
// 	// Fallback: use original order
// 	return {husbandId: idA, wifeId: idB};
// };
//
// // ===================================================================
// // Main layout function
// // ===================================================================
//
// /**
//  * Computes a hierarchical family tree layout using Dagre.
//  *
//  * v3 improvements:
//  * - Consistent husband-left, wife-right ordering
//  * - Dynamic node width estimation
//  * - Wider couple gap for relationship labels
//  * - Top-down recursive child centering
//  * - Handles hundreds of nodes efficiently
//  * - Optional React Flow instance for actual node dimensions
//  *
//  * @param {Array}  nodes       - React Flow nodes
//  * @param {Array}  edges       - React Flow edges
//  * @param {Object} opts        - Layout config
//  * @param {Object} rfInstance  - Optional React Flow instance for measured dimensions
//  * @returns {{ nodes: Array, edges: Array }}
//  */
// export const computeDagreLayout = (nodes, edges, opts = {}, rfInstance = null) => {
// 	const {
// 		direction = "TB",
// 		nodeSpacing = 100,
// 		rankSpacing = 200,
// 		coupleGap = COUPLE_LABEL_WIDTH,
// 	} = opts;
//
// 	if (!nodes.length) return {nodes, edges};
//
// 	// Build lookups
// 	const nodeMap = {};
// 	const nodeSizes = {};
// 	nodes.forEach((n) => {
// 		const id = n.id || n._id;
// 		nodeMap[id] = n;
// 		nodeSizes[id] = {
// 			w: estimateNodeWidth(n, rfInstance),
// 			h: estimateNodeHeight(n, rfInstance),
// 		};
// 	});
//
// 	// ---------------------------------------------------------------
// 	// STEP 1: Identify spouse pairs with consistent ordering
// 	// ---------------------------------------------------------------
// 	const spousePairs = [];
// 	const nodeToCouple = {};
// 	const processedSpouseEdges = new Set();
//
// 	edges.forEach((edge) => {
// 		const rel = edge.data?.relationshipType;
// 		if (!SPOUSE_RELATIONS.has(rel)) return;
//
// 		const pairKey = [edge.source, edge.target].sort().join("::");
// 		if (processedSpouseEdges.has(pairKey)) return;
// 		processedSpouseEdges.add(pairKey);
//
// 		const {husbandId, wifeId} = getSpouseOrder(edges, edge.source, edge.target);
// 		const coupleId = `couple::${husbandId}::${wifeId}`;
//
// 		spousePairs.push({
// 			id: coupleId,
// 			husbandId,
// 			wifeId,
// 		});
// 		nodeToCouple[husbandId] = coupleId;
// 		nodeToCouple[wifeId] = coupleId;
// 	});
//
// 	// ---------------------------------------------------------------
// 	// STEP 2: Build Dagre graph — couples as compound nodes
// 	// ---------------------------------------------------------------
// 	const g = new dagre.graphlib.Graph();
// 	g.setDefaultEdgeLabel(() => ({}));
// 	g.setGraph({
// 		rankdir: direction,
// 		nodesep: nodeSpacing,
// 		ranksep: rankSpacing,
// 		edgesep: 50,
// 		marginx: 60,
// 		marginy: 60,
// 	});
//
// 	const inCouple = new Set();
//
// 	spousePairs.forEach((couple) => {
// 		inCouple.add(couple.husbandId);
// 		inCouple.add(couple.wifeId);
//
// 		const hW = nodeSizes[couple.husbandId]?.w || DEFAULT_NODE_WIDTH;
// 		const wW = nodeSizes[couple.wifeId]?.w || DEFAULT_NODE_WIDTH;
// 		const coupleWidth = hW + wW + coupleGap;
// 		const coupleHeight = Math.max(
// 			nodeSizes[couple.husbandId]?.h || DEFAULT_NODE_HEIGHT,
// 			nodeSizes[couple.wifeId]?.h || DEFAULT_NODE_HEIGHT
// 		);
//
// 		g.setNode(couple.id, {width: coupleWidth, height: coupleHeight});
// 	});
//
// 	// Singleton nodes
// 	nodes.forEach((node) => {
// 		const id = node.id || node._id;
// 		if (!inCouple.has(id)) {
// 			g.setNode(id, {
// 				width: nodeSizes[id]?.w || DEFAULT_NODE_WIDTH,
// 				height: nodeSizes[id]?.h || DEFAULT_NODE_HEIGHT,
// 			});
// 		}
// 	});
//
// 	// ---------------------------------------------------------------
// 	// STEP 3: Add hierarchical edges
// 	// ---------------------------------------------------------------
// 	const resolveNode = (id) => nodeToCouple[id] || id;
// 	const addedEdges = new Set();
//
// 	edges.forEach((edge) => {
// 		const rel = edge.data?.relationshipType;
// 		if (!rel) return;
// 		if (SPOUSE_RELATIONS.has(rel) || HORIZONTAL_RELATIONS.has(rel)) return;
//
// 		let parentId, childId;
//
// 		if (PARENT_TO_CHILD.has(rel)) {
// 			parentId = edge.source;
// 			childId = edge.target;
// 		} else if (CHILD_TO_PARENT.has(rel)) {
// 			parentId = edge.target;
// 			childId = edge.source;
// 		} else {
// 			return;
// 		}
//
// 		const resolvedParent = resolveNode(parentId);
// 		const resolvedChild = resolveNode(childId);
//
// 		if (resolvedParent === resolvedChild) return;
//
// 		const edgeKey = `${resolvedParent}→${resolvedChild}`;
// 		if (addedEdges.has(edgeKey)) return;
// 		addedEdges.add(edgeKey);
//
// 		if (g.hasNode(resolvedParent) && g.hasNode(resolvedChild)) {
// 			g.setEdge(resolvedParent, resolvedChild, {weight: 2});
// 		}
// 	});
//
// 	// ---------------------------------------------------------------
// 	// STEP 4: Run Dagre
// 	// ---------------------------------------------------------------
// 	dagre.layout(g);
//
// 	// ---------------------------------------------------------------
// 	// STEP 5: Extract positions, split couples (husband LEFT, wife RIGHT)
// 	// ---------------------------------------------------------------
// 	const posMap = {};
//
// 	// Singletons
// 	nodes.forEach((node) => {
// 		const id = node.id || node._id;
// 		if (inCouple.has(id)) return;
//
// 		const dagreNode = g.node(id);
// 		const w = nodeSizes[id]?.w || DEFAULT_NODE_WIDTH;
// 		const h = nodeSizes[id]?.h || DEFAULT_NODE_HEIGHT;
//
// 		if (dagreNode) {
// 			posMap[id] = {
// 				x: dagreNode.x - w / 2,
// 				y: dagreNode.y - h / 2,
// 			};
// 		} else {
// 			posMap[id] = node.position || {x: 0, y: 0};
// 		}
// 	});
//
// 	// Couples — husband always left, wife always right
// 	spousePairs.forEach((couple) => {
// 		const dagreNode = g.node(couple.id);
// 		if (!dagreNode) {
// 			posMap[couple.husbandId] = nodeMap[couple.husbandId]?.position || {x: 0, y: 0};
// 			posMap[couple.wifeId] = nodeMap[couple.wifeId]?.position || {x: 0, y: 0};
// 			return;
// 		}
//
// 		const hW = nodeSizes[couple.husbandId]?.w || DEFAULT_NODE_WIDTH;
// 		const wW = nodeSizes[couple.wifeId]?.w || DEFAULT_NODE_WIDTH;
// 		const hH = nodeSizes[couple.husbandId]?.h || DEFAULT_NODE_HEIGHT;
// 		const wH = nodeSizes[couple.wifeId]?.h || DEFAULT_NODE_HEIGHT;
//
// 		const centerX = dagreNode.x;
// 		const totalWidth = hW + coupleGap + wW;
//
// 		// Husband on left
// 		posMap[couple.husbandId] = {
// 			x: centerX - totalWidth / 2,
// 			y: dagreNode.y - hH / 2,
// 		};
//
// 		// Wife on right
// 		posMap[couple.wifeId] = {
// 			x: centerX - totalWidth / 2 + hW + coupleGap,
// 			y: dagreNode.y - wH / 2,
// 		};
// 	});
//
// 	// ---------------------------------------------------------------
// 	// STEP 6: Top-down recursive child centering
// 	// ---------------------------------------------------------------
// 	// Build parent→children map
// 	const parentToChildren = {};
// 	const childToParent = {};
//
// 	edges.forEach((edge) => {
// 		const rel = edge.data?.relationshipType;
// 		if (!rel) return;
// 		if (SPOUSE_RELATIONS.has(rel) || HORIZONTAL_RELATIONS.has(rel)) return;
//
// 		let parentId, childId;
// 		if (PARENT_TO_CHILD.has(rel)) {
// 			parentId = edge.source;
// 			childId = edge.target;
// 		} else if (CHILD_TO_PARENT.has(rel)) {
// 			parentId = edge.target;
// 			childId = edge.source;
// 		} else {
// 			return;
// 		}
//
// 		const rParent = resolveNode(parentId);
// 		const rChild = resolveNode(childId);
// 		if (rParent === rChild) return;
//
// 		if (!parentToChildren[rParent]) parentToChildren[rParent] = new Set();
// 		parentToChildren[rParent].add(rChild);
// 		childToParent[rChild] = rParent;
// 	});
//
// 	// Find root nodes (parents with no parent of their own)
// 	const allParents = new Set(Object.keys(parentToChildren));
// 	const allChildren = new Set(Object.keys(childToParent));
// 	const roots = [...allParents].filter((p) => !allChildren.has(p));
//
// 	// Also include orphan nodes (no parent, no children)
// 	const allResolvedNodes = new Set();
// 	nodes.forEach((n) => {
// 		const id = n.id || n._id;
// 		allResolvedNodes.add(resolveNode(id));
// 	});
// 	const orphans = [...allResolvedNodes].filter(
// 		(id) => !allParents.has(id) && !allChildren.has(id)
// 	);
//
// 	// Helper: get the X-extent of a resolved node (couple or single)
// 	const getNodeExtent = (resolvedId) => {
// 		if (resolvedId.startsWith("couple::")) {
// 			const couple = spousePairs.find((c) => c.id === resolvedId);
// 			if (!couple) return {minX: 0, maxX: 0};
// 			const hPos = posMap[couple.husbandId];
// 			const wPos = posMap[couple.wifeId];
// 			const hW = nodeSizes[couple.husbandId]?.w || DEFAULT_NODE_WIDTH;
// 			const wW = nodeSizes[couple.wifeId]?.w || DEFAULT_NODE_WIDTH;
// 			if (!hPos || !wPos) return {minX: 0, maxX: 0};
// 			return {
// 				minX: Math.min(hPos.x, wPos.x),
// 				maxX: Math.max(hPos.x + hW, wPos.x + wW),
// 			};
// 		} else {
// 			const pos = posMap[resolvedId];
// 			const w = nodeSizes[resolvedId]?.w || DEFAULT_NODE_WIDTH;
// 			if (!pos) return {minX: 0, maxX: 0};
// 			return {minX: pos.x, maxX: pos.x + w};
// 		}
// 	};
//
// 	// Helper: shift all members of a resolved node by dx
// 	const shiftNode = (resolvedId, dx) => {
// 		if (Math.abs(dx) < 1) return; // Skip negligible shifts
//
// 		if (resolvedId.startsWith("couple::")) {
// 			const couple = spousePairs.find((c) => c.id === resolvedId);
// 			if (couple) {
// 				if (posMap[couple.husbandId]) posMap[couple.husbandId].x += dx;
// 				if (posMap[couple.wifeId]) posMap[couple.wifeId].x += dx;
// 			}
// 		} else {
// 			if (posMap[resolvedId]) posMap[resolvedId].x += dx;
// 		}
// 	};
//
// 	// Recursive centering: process tree top-down
// 	const centerChildren = (parentKey) => {
// 		const childSet = parentToChildren[parentKey];
// 		if (!childSet || childSet.size === 0) return;
//
// 		const children = [...childSet];
//
// 		// First, recursively center grandchildren under each child
// 		children.forEach((child) => centerChildren(child));
//
// 		// Now center this set of children under the parent
// 		const parentExtent = getNodeExtent(parentKey);
// 		const parentCenterX = (parentExtent.minX + parentExtent.maxX) / 2;
//
// 		// Compute children's combined extent
// 		let childMinX = Infinity, childMaxX = -Infinity;
// 		children.forEach((child) => {
// 			const ext = getNodeExtent(child);
// 			childMinX = Math.min(childMinX, ext.minX);
// 			childMaxX = Math.max(childMaxX, ext.maxX);
// 		});
//
// 		if (childMinX === Infinity) return;
//
// 		const childCenterX = (childMinX + childMaxX) / 2;
// 		const shiftX = parentCenterX - childCenterX;
//
// 		// Shift all children (and their entire subtrees) by shiftX
// 		const shiftSubtree = (nodeKey, dx) => {
// 			shiftNode(nodeKey, dx);
// 			const subChildren = parentToChildren[nodeKey];
// 			if (subChildren) {
// 				[...subChildren].forEach((sc) => shiftSubtree(sc, dx));
// 			}
// 		};
//
// 		children.forEach((child) => shiftSubtree(child, shiftX));
// 	};
//
// 	// Run centering from each root
// 	roots.forEach((root) => centerChildren(root));
//
// 	// ---------------------------------------------------------------
// 	// STEP 7: Overlap resolution — push apart any siblings that overlap
// 	// ---------------------------------------------------------------
// 	Object.keys(parentToChildren).forEach((parentKey) => {
// 		const children = [...(parentToChildren[parentKey] || [])];
// 		if (children.length < 2) return;
//
// 		// Sort children by their X position
// 		children.sort((a, b) => {
// 			const extA = getNodeExtent(a);
// 			const extB = getNodeExtent(b);
// 			return extA.minX - extB.minX;
// 		});
//
// 		// Check each adjacent pair for overlap
// 		const MIN_GAP = 30;
// 		for (let i = 0; i < children.length - 1; i++) {
// 			const extCurr = getNodeExtent(children[i]);
// 			const extNext = getNodeExtent(children[i + 1]);
//
// 			const overlap = extCurr.maxX + MIN_GAP - extNext.minX;
// 			if (overlap > 0) {
// 				// Push the next child (and its subtree) to the right
// 				const shiftSubtree = (nodeKey, dx) => {
// 					shiftNode(nodeKey, dx);
// 					const subChildren = parentToChildren[nodeKey];
// 					if (subChildren) {
// 						[...subChildren].forEach((sc) => shiftSubtree(sc, dx));
// 					}
// 				};
// 				// Shift right half to the right, left half to the left
// 				for (let j = i + 1; j < children.length; j++) {
// 					shiftSubtree(children[j], overlap / 2);
// 				}
// 				for (let j = 0; j <= i; j++) {
// 					shiftSubtree(children[j], -overlap / 2);
// 				}
// 			}
// 		}
// 	});
//
// 	// Re-run centering after overlap resolution
// 	roots.forEach((root) => centerChildren(root));
//
// 	// ---------------------------------------------------------------
// 	// STEP 8: Apply positions to nodes
// 	// ---------------------------------------------------------------
// 	const layoutedNodes = nodes.map((node) => {
// 		const id = node.id || node._id;
// 		return {
// 			...node,
// 			position: posMap[id] || node.position || {x: 0, y: 0},
// 		};
// 	});
//
// 	return {nodes: layoutedNodes, edges};
// };

import dagre from "dagre";

// -------------------------------------------------------------------
// Defaults (used when actual measurements unavailable)
// -------------------------------------------------------------------
const DEFAULT_NODE_WIDTH = 280;
const DEFAULT_NODE_HEIGHT = 85;

// -------------------------------------------------------------------
// Relationship classification (matches relation.jsx)
// -------------------------------------------------------------------
const SPOUSE_RELATIONS = new Set(["husband-wife", "wife-husband"]);

const PARENT_TO_CHILD = new Set([
	"father", "mother", "father-in-law", "mother-in-law",
]);

const CHILD_TO_PARENT = new Set([
	"son", "daughter", "son-in-law", "daughter-in-law",
	"adopted-son", "adopted-daughter",
]);

const HORIZONTAL_RELATIONS = new Set([
	"siblings", "brother", "sister",
	"brother-in-law", "sister-in-law",
]);

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

/**
 * Get node width — prefer actual rendered size, then estimate from name.
 */
const getNodeWidth = (node, rfInstance) => {
	const id = node.id || node._id;
	if (rfInstance) {
		const rfNode = rfInstance.getNode(id);
		if (rfNode?.width) return rfNode.width;
	}
	const firstName = node.data?.firstName || "";
	const middleName = node.data?.middleName || "";
	const nameLen = `${firstName} ${middleName}`.length;
	return Math.max(220, Math.min(360, nameLen * 9 + 120));
};

/**
 * Get node height — prefer actual rendered size.
 */
const getNodeHeight = (node, rfInstance) => {
	const id = node.id || node._id;
	if (rfInstance) {
		const rfNode = rfInstance.getNode(id);
		if (rfNode?.height) return rfNode.height;
	}
	return DEFAULT_NODE_HEIGHT;
};

// ===================================================================
// Main layout function
// ===================================================================

/**
 * Computes a hierarchical family tree layout using Dagre.
 *
 * v4 improvements:
 * - Gender-based spouse ordering (Male=left, Female=right) — no edge-order dependency
 * - Fully dynamic couple gap based on actual node widths
 * - Robust top-down recursive centering with subtree shifting
 * - Overlap resolution pass for sibling groups
 * - Efficient for trees with hundreds of nodes
 *
 * @param {Array}  nodes       - React Flow nodes
 * @param {Array}  edges       - React Flow edges
 * @param {Object} opts        - Layout config overrides
 * @param {Object} rfInstance  - Optional React Flow instance for measured dimensions
 * @returns {{ nodes: Array, edges: Array }}
 */
export const computeDagreLayout = (nodes, edges, opts = {}, rfInstance = null) => {
	const {
		direction = "TB",
		nodeSpacing = 100,
		rankSpacing = 200,
		coupleLabelWidth = 120,  // Min space between spouses for the label
	} = opts;

	if (!nodes.length) return {nodes, edges};

	// ----- Build lookups -----
	const nodeMap = {};
	const sizes = {};
	nodes.forEach((n) => {
		const id = n.id || n._id;
		nodeMap[id] = n;
		sizes[id] = {
			w: getNodeWidth(n, rfInstance),
			h: getNodeHeight(n, rfInstance),
		};
	});

	// ---------------------------------------------------------------
	// STEP 1: Identify spouse pairs using GENDER for ordering
	// ---------------------------------------------------------------
	const spousePairs = [];
	const nodeToCouple = {};
	const seenSpousePairs = new Set();

	edges.forEach((edge) => {
		const rel = edge.data?.relationshipType;
		if (!SPOUSE_RELATIONS.has(rel)) return;

		const pairKey = [edge.source, edge.target].sort().join("::");
		if (seenSpousePairs.has(pairKey)) return;
		seenSpousePairs.add(pairKey);

		const nodeA = nodeMap[edge.source];
		const nodeB = nodeMap[edge.target];

		// Determine who is male (left) and female (right) using gender data
		let leftId, rightId;
		const genderA = nodeA?.data?.gender;
		const genderB = nodeB?.data?.gender;

		if (genderA === "Male" && genderB === "Female") {
			leftId = edge.source;
			rightId = edge.target;
		} else if (genderA === "Female" && genderB === "Male") {
			leftId = edge.target;
			rightId = edge.source;
		} else {
			// Same gender or unknown — keep source/target order
			leftId = edge.source;
			rightId = edge.target;
		}

		const coupleId = `couple::${leftId}::${rightId}`;
		spousePairs.push({id: coupleId, leftId, rightId});
		nodeToCouple[leftId] = coupleId;
		nodeToCouple[rightId] = coupleId;
	});

	// ---------------------------------------------------------------
	// STEP 2: Build Dagre graph — couples as compound nodes
	// ---------------------------------------------------------------
	const g = new dagre.graphlib.Graph();
	g.setDefaultEdgeLabel(() => ({}));
	g.setGraph({
		rankdir: direction,
		nodesep: nodeSpacing,
		ranksep: rankSpacing,
		edgesep: 50,
		marginx: 60,
		marginy: 60,
	});

	const inCouple = new Set();

	spousePairs.forEach((couple) => {
		inCouple.add(couple.leftId);
		inCouple.add(couple.rightId);

		const lw = sizes[couple.leftId]?.w || DEFAULT_NODE_WIDTH;
		const rw = sizes[couple.rightId]?.w || DEFAULT_NODE_WIDTH;
		// Dynamic couple width = left node + label space + right node
		const coupleWidth = lw + coupleLabelWidth + rw;
		const coupleHeight = Math.max(
			sizes[couple.leftId]?.h || DEFAULT_NODE_HEIGHT,
			sizes[couple.rightId]?.h || DEFAULT_NODE_HEIGHT
		);

		g.setNode(couple.id, {width: coupleWidth, height: coupleHeight});
	});

	// Add singleton nodes
	nodes.forEach((node) => {
		const id = node.id || node._id;
		if (!inCouple.has(id)) {
			g.setNode(id, {
				width: sizes[id]?.w || DEFAULT_NODE_WIDTH,
				height: sizes[id]?.h || DEFAULT_NODE_HEIGHT,
			});
		}
	});

	// ---------------------------------------------------------------
	// STEP 3: Add hierarchical edges to Dagre
	// ---------------------------------------------------------------
	const resolveNode = (id) => nodeToCouple[id] || id;
	const dagEdges = new Set();

	edges.forEach((edge) => {
		const rel = edge.data?.relationshipType;
		if (!rel) return;
		if (SPOUSE_RELATIONS.has(rel) || HORIZONTAL_RELATIONS.has(rel)) return;

		let parentId, childId;
		if (PARENT_TO_CHILD.has(rel)) {
			parentId = edge.source;
			childId = edge.target;
		} else if (CHILD_TO_PARENT.has(rel)) {
			parentId = edge.target;
			childId = edge.source;
		} else {
			return;
		}

		const rp = resolveNode(parentId);
		const rc = resolveNode(childId);
		if (rp === rc) return;

		const ek = `${rp}→${rc}`;
		if (dagEdges.has(ek)) return;
		dagEdges.add(ek);

		if (g.hasNode(rp) && g.hasNode(rc)) {
			g.setEdge(rp, rc, {weight: 2});
		}
	});

	// ---------------------------------------------------------------
	// STEP 4: Run Dagre layout
	// ---------------------------------------------------------------
	dagre.layout(g);

	// ---------------------------------------------------------------
	// STEP 5: Extract positions — split couples (male LEFT, female RIGHT)
	// ---------------------------------------------------------------
	const pos = {};

	// Singletons
	nodes.forEach((node) => {
		const id = node.id || node._id;
		if (inCouple.has(id)) return;
		const dn = g.node(id);
		const w = sizes[id]?.w || DEFAULT_NODE_WIDTH;
		const h = sizes[id]?.h || DEFAULT_NODE_HEIGHT;
		if (dn) {
			pos[id] = {x: dn.x - w / 2, y: dn.y - h / 2};
		} else {
			pos[id] = node.position || {x: 0, y: 0};
		}
	});

	// Couples
	spousePairs.forEach((couple) => {
		const dn = g.node(couple.id);
		if (!dn) {
			pos[couple.leftId] = nodeMap[couple.leftId]?.position || {x: 0, y: 0};
			pos[couple.rightId] = nodeMap[couple.rightId]?.position || {x: 0, y: 0};
			return;
		}

		const lw = sizes[couple.leftId]?.w || DEFAULT_NODE_WIDTH;
		const rw = sizes[couple.rightId]?.w || DEFAULT_NODE_WIDTH;
		const lh = sizes[couple.leftId]?.h || DEFAULT_NODE_HEIGHT;
		const rh = sizes[couple.rightId]?.h || DEFAULT_NODE_HEIGHT;
		const totalWidth = lw + coupleLabelWidth + rw;

		// Left member (male)
		pos[couple.leftId] = {
			x: dn.x - totalWidth / 2,
			y: dn.y - lh / 2,
		};
		// Right member (female)
		pos[couple.rightId] = {
			x: dn.x - totalWidth / 2 + lw + coupleLabelWidth,
			y: dn.y - rh / 2,
		};
	});

	// ---------------------------------------------------------------
	// STEP 6: Build parent→children tree for centering
	// ---------------------------------------------------------------
	const parentChildren = {};  // resolvedParent → Set<resolvedChild>

	edges.forEach((edge) => {
		const rel = edge.data?.relationshipType;
		if (!rel) return;
		if (SPOUSE_RELATIONS.has(rel) || HORIZONTAL_RELATIONS.has(rel)) return;

		let parentId, childId;
		if (PARENT_TO_CHILD.has(rel)) {
			parentId = edge.source;
			childId = edge.target;
		} else if (CHILD_TO_PARENT.has(rel)) {
			parentId = edge.target;
			childId = edge.source;
		} else {
			return;
		}

		const rp = resolveNode(parentId);
		const rc = resolveNode(childId);
		if (rp === rc) return;

		if (!parentChildren[rp]) parentChildren[rp] = new Set();
		parentChildren[rp].add(rc);
	});

	// Find roots (nodes that are parents but not children of anyone)
	const allChildKeys = new Set();
	Object.values(parentChildren).forEach((cs) => cs.forEach((c) => allChildKeys.add(c)));
	const roots = Object.keys(parentChildren).filter((p) => !allChildKeys.has(p));

	// ---------------------------------------------------------------
	// STEP 7: Extent + shift helpers
	// ---------------------------------------------------------------
	const getExtent = (rId) => {
		if (rId.startsWith("couple::")) {
			const c = spousePairs.find((cp) => cp.id === rId);
			if (!c) return {min: 0, max: 0};
			const lp = pos[c.leftId];
			const rp = pos[c.rightId];
			const lw = sizes[c.leftId]?.w || DEFAULT_NODE_WIDTH;
			const rw = sizes[c.rightId]?.w || DEFAULT_NODE_WIDTH;
			if (!lp || !rp) return {min: 0, max: 0};
			return {
				min: Math.min(lp.x, rp.x),
				max: Math.max(lp.x + lw, rp.x + rw),
			};
		}
		const p = pos[rId];
		const w = sizes[rId]?.w || DEFAULT_NODE_WIDTH;
		if (!p) return {min: 0, max: 0};
		return {min: p.x, max: p.x + w};
	};

	const getCenterX = (rId) => {
		const e = getExtent(rId);
		return (e.min + e.max) / 2;
	};

	const shiftNode = (rId, dx) => {
		if (Math.abs(dx) < 0.5) return;
		if (rId.startsWith("couple::")) {
			const c = spousePairs.find((cp) => cp.id === rId);
			if (c) {
				if (pos[c.leftId]) pos[c.leftId].x += dx;
				if (pos[c.rightId]) pos[c.rightId].x += dx;
			}
		} else {
			if (pos[rId]) pos[rId].x += dx;
		}
	};

	// Shift a node and its entire subtree
	const shiftSubtree = (rId, dx) => {
		if (Math.abs(dx) < 0.5) return;
		shiftNode(rId, dx);
		const kids = parentChildren[rId];
		if (kids) [...kids].forEach((k) => shiftSubtree(k, dx));
	};

	// ---------------------------------------------------------------
	// STEP 8: Top-down recursive centering + overlap fix
	// ---------------------------------------------------------------
	const MIN_SIBLING_GAP = 40;

	const layoutSubtree = (parentKey) => {
		const kids = parentChildren[parentKey];
		if (!kids || kids.size === 0) return;

		const children = [...kids];

		// First, recursively layout each child's subtree
		children.forEach((child) => layoutSubtree(child));

		// Sort children by current X position (left to right)
		children.sort((a, b) => getCenterX(a) - getCenterX(b));

		// --- Fix overlaps between adjacent siblings ---
		for (let i = 0; i < children.length - 1; i++) {
			const extCurr = getExtent(children[i]);
			const extNext = getExtent(children[i + 1]);
			const overlap = extCurr.max + MIN_SIBLING_GAP - extNext.min;

			if (overlap > 0) {
				// Push right sibling (and subtree) to the right
				for (let j = i + 1; j < children.length; j++) {
					shiftSubtree(children[j], overlap);
				}
			}
		}

		// --- Center children group under parent ---
		const parentCX = getCenterX(parentKey);

		// Compute children's combined extent
		let groupMin = Infinity, groupMax = -Infinity;
		children.forEach((child) => {
			const ext = getExtent(child);
			groupMin = Math.min(groupMin, ext.min);
			groupMax = Math.max(groupMax, ext.max);
		});

		if (groupMin === Infinity) return;

		const groupCX = (groupMin + groupMax) / 2;
		const dx = parentCX - groupCX;

		// Shift ALL children subtrees by the same amount
		children.forEach((child) => shiftSubtree(child, dx));
	};

	// Run from each root
	roots.forEach((root) => layoutSubtree(root));

	// ---------------------------------------------------------------
	// STEP 9: Final overlap check (across all siblings at each level)
	//         This catches cross-family overlaps Dagre missed
	// ---------------------------------------------------------------
	// Group all resolved nodes by their Y position (rank/generation)
	const rankGroups = {};
	const allResolved = new Set();

	nodes.forEach((n) => {
		const id = n.id || n._id;
		const rId = resolveNode(id);
		if (allResolved.has(rId)) return;
		allResolved.add(rId);

		const p = rId.startsWith("couple::")
			? pos[spousePairs.find((c) => c.id === rId)?.leftId]
			: pos[rId];
		if (!p) return;

		const rankKey = Math.round(p.y / 50) * 50; // Group by approximate Y
		if (!rankGroups[rankKey]) rankGroups[rankKey] = [];
		rankGroups[rankKey].push(rId);
	});

	Object.values(rankGroups).forEach((group) => {
		if (group.length < 2) return;
		group.sort((a, b) => getCenterX(a) - getCenterX(b));

		for (let i = 0; i < group.length - 1; i++) {
			const extCurr = getExtent(group[i]);
			const extNext = getExtent(group[i + 1]);
			const overlap = extCurr.max + MIN_SIBLING_GAP - extNext.min;

			if (overlap > 0) {
				shiftSubtree(group[i + 1], overlap);
			}
		}
	});

	// ---------------------------------------------------------------
	// STEP 10: Apply positions to nodes
	// ---------------------------------------------------------------
	const layoutedNodes = nodes.map((node) => {
		const id = node.id || node._id;
		return {
			...node,
			position: pos[id] || node.position || {x: 0, y: 0},
		};
	});

	return {nodes: layoutedNodes, edges};
};